/**
 * Campaign — single source of truth for all campaign figures and dates.
 * Source: /Docs/04_funding_and_outreach/Person_Foundation_Funding_Roadmap.docx
 *         /Docs/02_technical_specification/person_protocol_infra_costing.html
 *         /Docs/04_funding_and_outreach/person-outreach/CLAUDE.md
 *
 * Do NOT hardcode these figures in components. Import from here.
 */

export const FOUNDATION_NAME = "The .person Foundation" as const;
export const FOUNDATION_DOMAIN = "dotperson.org" as const;
export const FOUNDATION_EMAIL = "hello@dotperson.org" as const;

export const FOUNDER_NAME = "Humphrey Theodore K. Ng'ambi" as const;
export const FOUNDER_SHORT = "TK" as const;
// FOUNDER_LOCATION is TK's personal location. Used for the founder's
// Person JSON-LD address (FounderPersonSchema) and for the example
// /profile/tk "Place of origin" field. NOT used for institutional
// framing of the Foundation. TK's canonical biographical page lives
// at https://humphreytheodore.com/about.
export const FOUNDER_LOCATION = "Lusaka, Zambia" as const;

// FOUNDATION_LEADERSHIP is how the Foundation frames itself. Personhood
// is not regional; the Foundation's leadership is aspirationally global.
// Used in Foundation status panels, the Home hero metadata, and SEO.
export const FOUNDATION_LEADERSHIP = "Distributed" as const;

export const MOTTO = "Dum spiro, spero. While I breathe, I hope." as const;

// ---------------------------------------------------------------------------
// Stewardship — Mind Hack, Inc. acts as the temporary fiduciary steward for
// all monetary support to the Foundation until the IRS 501(c)(3) determination
// arrives. Mind Hack, Inc. is a Delaware C-corporation, owned by the Founding
// Director. Banked at Mercury (Column N.A.).
//
// Wire numbers below are surfaced on the public Contribute page via a
// click-to-reveal panel; the same numbers also live in the gated PDF.
// PREREQUISITE: Mercury ACH debit-block must be enabled before this ships
// to production.
// ---------------------------------------------------------------------------
export const STEWARD_ENTITY = "Mind Hack, Inc." as const;
export const STEWARD_ENTITY_ADDRESS =
  "651 N Broad St, Ste 201, Middletown, DE 19709, USA" as const;
export const STEWARD_BANK = "Column N.A. (banking partner of Mercury)" as const;
export const STEWARD_BANK_ADDRESS =
  "1 Letterman Drive, Building A, Suite A4-700, San Francisco, CA 94129, USA" as const;
export const STEWARD_SWIFT = "CLNOUS66MER" as const;
export const STEWARD_SWIFT_INTERMEDIARY = "CHASUS33XXX" as const; // JPMorgan Chase
export const STEWARD_ACCOUNT_TYPE = "Checking (USD)" as const;
export const STEWARD_ABA = "121145433" as const;
export const STEWARD_ABA_FALLBACK = "121145307" as const;
export const STEWARD_ACCOUNT_NUMBER = "341986994599287" as const;
export const STEWARD_WIRE_PDF_PATH =
  "/downloads/dotperson-stewardship-wire-details.pdf" as const;
export const STEWARD_MEMO_PREFIX = "DPF-" as const;
export const TAX_DEDUCTIBLE = false as const; // flips to true after IRS determination
export const STEWARDSHIP_BEGAN_ISO = "2026-05-04" as const;

// ICANN — first new-gTLD window since 2012.
export const ICANN_OPEN_ISO = "2026-04-30" as const;
export const ICANN_CLOSE_ISO = "2026-08-12" as const;
export const ICANN_OPEN_LABEL = "30 April 2026" as const;
export const ICANN_CLOSE_LABEL = "12 August 2026" as const;

// Campaign target — $320,000 USD total.
export const CAMPAIGN_TOTAL_USD = 320_000 as const;
export const CAMPAIGN_TOTAL_LABEL = "US$320,000" as const;

export interface AllocationLine {
  amount_usd: number;
  label: string;
  short: string;
  detail: string;
}

export const CAMPAIGN_ALLOCATION: readonly AllocationLine[] = [
  {
    amount_usd: 227_000,
    label: "US$227,000",
    short: "ICANN application fee",
    detail:
      "Non-negotiable, paid at filing. The ICANN new-gTLD evaluation fee that secures .person under mission-locked stewardship.",
  },
  {
    amount_usd: 30_000,
    label: "US$30,000",
    short: "Legal counsel",
    detail:
      "ICANN filing preparation, 501(c)(3) finalisation, trademark.",
  },
  {
    amount_usd: 25_000,
    label: "US$25,000",
    short: "Infrastructure scaffold",
    detail:
      "Registry Service Provider onboarding deposits, hosting, initial security audits.",
  },
  {
    amount_usd: 20_000,
    label: "US$20,000",
    short: "Continued Operations Instrument",
    detail:
      "Financial guarantee required by ICANN to protect registrants if the registry fails to operate.",
  },
  {
    amount_usd: 18_000,
    label: "US$18,000",
    short: "Founding Director runway",
    detail:
      "Six to nine months of runway covering the ICANN evaluation period.",
  },
] as const;

// Assert the numbers add up.
export const CAMPAIGN_ALLOCATION_SUM: number = CAMPAIGN_ALLOCATION.reduce(
  (sum, line) => sum + line.amount_usd,
  0
);

// Tier labels from the outreach go-bag.
export const SUPPORT_TIERS = [
  { label: "Named Supporter", amount_usd: 25 },
  { label: "Contributor", amount_usd: 100 },
  { label: "Advocate", amount_usd: 500 },
  { label: "Patron", amount_usd: 1_000 },
  { label: "Founding Supporter", amount_usd: 5_000 },
  { label: "Cornerstone", amount_usd: 10_000 },
] as const;

export function daysUntilISO(iso: string, now: Date = new Date()): number {
  const target = new Date(iso + "T00:00:00Z").getTime();
  const today = new Date(now.toISOString().slice(0, 10) + "T00:00:00Z").getTime();
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}
